import { createSelector, createSlice } from '@reduxjs/toolkit';

import { logDatadogError } from '../services/datadog';
import { IndexedNutrition } from '../types/nutrition';
import { getMenus, getNutrition } from './thunks';
import type { Menu } from './types';

export type State = {
  loading: {
    menu: boolean;
    nutrition: boolean;
  };
  error: {
    menu: Nullable<string>;
    nutrition: Nullable<string>;
  };
  nutrition: Nullable<IndexedNutrition>;
  delivery: Nullable<Menu>;
  pickup: Nullable<Menu>;
};

const initialState: State = {
  loading: {
    menu: true,
    nutrition: true,
  },
  error: {
    menu: null,
    nutrition: null,
  },
  nutrition: null,
  delivery: null,
  pickup: null,
};

const slice = createSlice({
  name: 'menu',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(getNutrition.pending, (state) => {
        state.loading.nutrition = true;
        state.error.nutrition = null;
      })
      .addCase(getNutrition.fulfilled, (state, action) => {
        const { nutrition } = action.payload;
        state.nutrition = nutrition;
        state.loading.nutrition = false;
        state.error.nutrition = null;
      })
      .addCase(getNutrition.rejected, (state, action) => {
        const { error } = action;
        logDatadogError('Get Nutrition Error', {}, error);
        state.nutrition = null;
        state.loading.nutrition = false;
        state.error.nutrition = error.message ?? null;
      })
      .addCase(getMenus.pending, (state) => {
        state.loading.menu = true;
        state.error.menu = null;
      })
      .addCase(getMenus.fulfilled, (state, action) => {
        const { delivery, pickup } = action.payload;
        state.delivery = delivery;
        state.pickup = pickup;
        state.loading.menu = false;
        state.error.menu = null;
      })
      .addCase(getMenus.rejected, (state, action) => {
        const { error } = action;
        logDatadogError('Get Menus Error', {}, error);
        state.loading.menu = false;
        state.error.menu = error.message ?? null;
      });
  },
});

export const actions = slice.actions;
export default slice.reducer;

// Selectors

export const selectMenu = (state: { menu: State }) => state.menu;
export const selectNutrition = createSelector(selectMenu, (menu: State) => menu.nutrition ?? {});
